import { storageLevel } from '../shared/storage.js';
import { t, getLocale } from './i18n.js';
import { SharedMailboxes } from './SharedMailboxes.js';
import { MailLists } from './MailLists.js';
import { AuditLog } from './AuditLog.js';
import { ProIntegrations } from './ProIntegrations.js';
import { Subscription, PlatformSubscriptions } from './Subscription.js';
import { Operations } from './Operations.js';
import { MfaPolicy } from './MfaPolicy.js';
import { mfaRequired } from '../shared/mfa-policy.js';
import { useEffect, useState, useRef } from 'react';
import {
  Users,
  Globe,
  AtSign,
  ListChecks,
  ScrollText,
  Plus,
  ShieldCheck,
  ArrowUpRight,
  RefreshCw,
  Copy,
  Check,
  Trash2,
  Download,
  Server,
  Clock,
  Mail,
} from 'lucide-react';
import type { Bootstrap, Domain } from '../shared/types.js';
import { api, send } from './api.js';
import {
  Avatar,
  IconButton,
  Loading,
  Modal,
  Notice,
  formatBytes,
  displayDate,
} from './components.js';
export function Admin({
  bootstrap,
  notify,
  onQuotaChange,
}: {
  bootstrap: Bootstrap;
  onQuotaChange?: () => void;
  notify: (s: string) => void;
}) {
  const [tab, setTab] = useState('users'),
    [users, setUsers] = useState<any[]>([]),
    [domains, setDomains] = useState<Domain[]>([]),
    [aliases, setAliases] = useState<any[]>([]),
    [syncJobs, setSyncJobs] = useState<any[]>([]),
    [identityJobs, setIdentityJobs] = useState<any[]>([]),
    [identityEnabled, setIdentityEnabled] = useState(false),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(''),
    [modal, setModal] = useState(''),
    [busy, setBusy] = useState(false),
    [selectedDomain, setSelectedDomain] = useState<Domain | null>(null),
    [invitation, setInvitation] = useState<any>(null),
    [policy, setPolicy] = useState(bootstrap.organization.policy);
  const invitationKeys = useRef(new Map<string, string>());
  const [name, setName] = useState(''),
    [local, setLocal] = useState(''),
    [domain, setDomain] = useState(''),
    [recoveryEmail, setRecoveryEmail] = useState(''),
    [role, setRole] = useState('user'),
    [withMailbox, setWithMailbox] = useState(true),
    [inviteLocale, setInviteLocale] = useState<'fr' | 'en'>(getLocale()),
    [usage, setUsage] = useState<any>(),
    [target, setTarget] = useState('');
  const load = async () => {
    try {
      const [u, d, a, jobs, identity, auth] = await Promise.all([
        api('/admin/users'),
        api('/admin/domains'),
        api('/admin/aliases'),
        api('/admin/provisioning'),
        api('/admin/identity-actions'),
        api('/auth/config'),
      ]);
      setUsers(u);
      setDomains(d);
      setAliases(a);
      setSyncJobs(jobs);
      setIdentityJobs(identity);
      // The counters are informative: their absence never blocks the administration screen.
      void api('/admin/limits')
        .then(setUsage)
        .catch(() => setUsage(undefined));
      setIdentityEnabled(auth.identityInvitations);
      setDomain((x) => x || d[0]?.id || '');
      setTarget((x) => x || u[0]?.mailboxId || '');
      setError('');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const act = async (fn: () => Promise<any>, message: string) => {
    setBusy(true);
    setError('');
    try {
      const r = await fn();
      notify(message);
      await load();
      return r;
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    await act(async () => {
      if (modal === 'user') {
        const r = await send('/admin/users', {
          name,
          localPart: local,
          domainId: domain,
          recoveryEmail,
          role,
          withMailbox: role === 'user' || withMailbox,
          locale: inviteLocale,
        });
        setInvitation(r);
      } else if (modal === 'domain') await send('/admin/domains', { name });
      else await send('/aliases', { localPart: local, domainId: domain, mailboxId: target });
      setModal('');
      setName('');
      setLocal('');
      setRecoveryEmail('');
      setRole('user');
      setWithMailbox(true);
    }, t('Modification enregistrée.'));
  };
  const resend = (id: string) =>
    void act(async () => {
      const key = invitationKeys.current.get(id) ?? crypto.randomUUID();
      invitationKeys.current.set(id, key);
      const result = await send(`/users/${id}/invitation`, {}, 'POST', { 'Idempotency-Key': key });
      invitationKeys.current.delete(id);
      return result;
    }, t('Nouvelle invitation planifiée. Suivez son état dans Invitations.'));
  const inspect = async (d: Domain) => {
    setSelectedDomain(d);
  };
  const verify = async (d: Domain) => {
    const result = await act(
      () => send(`/domains/${d.id}/verify`, {}),
      t('Vérification du domaine terminée.'),
    );
    if (result) setSelectedDomain(result);
  };
  return (
    <main className="workspace-page">
      <header className="page-heading">
        <div>
          <span className="eyebrow">{t('VOTRE ORGANISATION')}</span>
          <h1>{t('Un espace bien administré.')}</h1>
          <p>
            {t('Gérez les accès et la messagerie de {organization}.', {
              organization: bootstrap.organization.name,
            })}
          </p>
        </div>
        <a className="secondary" href="/v1/admin/export">
          <Download size={17} />
          {t('Exporter la configuration')}
        </a>
      </header>
      {usage?.storage?.alert > 0 && (
        <Notice type={usage.storage.alert === 100 ? 'error' : 'info'}>
          {t(
            'Le stockage atteint {percent} % : libérez de l’espace ou demandez une extension. Aucun dépassement payant automatique.',
            { percent: usage.storage.alert },
          )}
        </Notice>
      )}
      <div className="stat-grid">
        <div className="stat-card">
          <span className="stat-icon violet">
            <Users size={21} />
          </span>
          <div>
            <small>{t('Collaborateurs')}</small>
            <strong>
              {users.length}
              <span>
                {t('{n} actifs', { n: users.filter((u) => u.status === 'active').length })}
              </span>
            </strong>
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-icon mint">
            <Globe size={21} />
          </span>
          <div>
            <small>{t('Domaines connectés')}</small>
            <strong>
              {domains.length}
              <span>
                {t('{n} prêts', { n: domains.filter((d) => d.routing === 'ready').length })}
              </span>
            </strong>
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-icon peach">
            <ShieldCheck size={21} />
          </span>
          <div>
            <small>{t('Double authentification')}</small>
            <strong>
              {users.filter((u) => u.mfaEnabled).length}
              <span>{t('sur {n} comptes', { n: users.length })}</span>
            </strong>
          </div>
        </div>
      </div>
      <section className="admin-card">
        <div className="admin-tabs">
          {[
            ['users', 'Collaborateurs', Users],
            ['subscription', 'Abonnement et stockage', Server],
            ['shared', 'Boîtes partagées', Mail],
            ['domains', 'Domaines', Globe],
            ['aliases', 'Alias', AtSign],
            ['lists', 'Listes', ListChecks],
            ['audit', 'Journal d’activité', ScrollText],
            ...(bootstrap.proCollaboration
              ? [['integrations', 'Intégrations Pro', ArrowUpRight] as const]
              : []),
            ['sync', 'Synchronisation', RefreshCw],
            ...(identityEnabled ? [['invitations', 'Invitations', Mail] as const] : []),
          ].map(([id, label, Icon]: any) => (
            <button key={id} className={tab === id ? 'active' : ''} onClick={() => setTab(id)}>
              <Icon size={17} />
              {t(label)}
            </button>
          ))}
        </div>
        <div className="section-heading">
          <div>
            <h2>
              {tab === 'shared'
                ? t('Accès aux boîtes d’équipe')
                : tab === 'integrations'
                  ? t('Vos intégrations métier')
                  : tab === 'subscription'
                    ? t('Votre capacité souscrite')
                    : tab === 'users'
                      ? t('Les membres de votre équipe')
                      : tab === 'domains'
                        ? t('Vos domaines professionnels')
                        : tab === 'aliases'
                          ? t('Une adresse, plusieurs possibilités')
                          : tab === 'lists'
                            ? t('Une adresse pour toute l’équipe')
                            : tab === 'sync'
                              ? t('Configuration des comptes')
                              : tab === 'invitations'
                                ? t('Activation des comptes')
                                : t('Les dernières actions')}
            </h2>
            <p>
              {tab === 'shared'
                ? t('Accès aux boîtes d’équipe')
                : tab === 'integrations'
                  ? t('Gérez les clés API et les webhooks de votre organisation.')
                  : tab === 'subscription'
                    ? t(
                        'Consultez les unités souscrites et répartissez leur stockage entre vos boîtes.',
                      )
                    : tab === 'users'
                      ? t('Chaque collaborateur dispose de sa propre boîte.')
                      : tab === 'domains'
                        ? t('La propriété et le routage sont vérifiés séparément.') +
                          (usage
                            ? ' ' +
                              t('{n} domaines raccordés sur {max} inclus.', {
                                n: usage.domains,
                                max: usage.limits.domains,
                              })
                            : '')
                        : tab === 'aliases'
                          ? t('Un alias reçoit les messages dans une boîte existante.') +
                            (usage
                              ? ' ' +
                                t('Jusqu’à {max} alias par boîte.', {
                                  max: usage.limits.aliasesPerMailbox,
                                })
                              : '')
                          : tab === 'lists'
                            ? t('Une liste interne distribue chaque message à ses membres.')
                            : tab === 'sync'
                              ? t(
                                  'Suivez les créations et modifications transmises aux services de messagerie et d’identité.',
                                )
                              : tab === 'invitations'
                                ? t(
                                    'Suivez la transmission des invitations et l’activation des collaborateurs.',
                                  )
                                : t(
                                    'Les actions sensibles restent traçables, sans accès au contenu des messages.',
                                  )}
            </p>
          </div>
          {['sync', 'invitations'].includes(tab) && (
            <button className="secondary small" onClick={() => void load()}>
              <RefreshCw size={16} />
              {t('Actualiser')}
            </button>
          )}
          {![
            'audit',
            'sync',
            'invitations',
            'subscription',
            'shared',
            'lists',
            'integrations',
          ].includes(tab) && (
            <button
              className="primary small"
              onClick={() => {
                setModal(tab === 'users' ? 'user' : tab === 'domains' ? 'domain' : 'alias');
                // Nothing of a previous invitation is reused: not its recovery address, not its role.
                setName('');
                setLocal('');
                setRecoveryEmail('');
                setRole('user');
                setWithMailbox(true);
                setInviteLocale(getLocale());
              }}
            >
              <Plus size={16} />
              {tab === 'users'
                ? t('Inviter un membre')
                : tab === 'domains'
                  ? t('Ajouter un domaine')
                  : t('Créer un alias')}
            </button>
          )}
        </div>
        {error && <Notice type="error">{error}</Notice>}
        {loading ? (
          <Loading />
        ) : tab === 'shared' ? (
          <SharedMailboxes notify={notify} />
        ) : tab === 'lists' ? (
          <MailLists notify={notify} />
        ) : tab === 'subscription' ? (
          <Subscription onQuotaChange={onQuotaChange} />
        ) : tab === 'integrations' ? (
          <ProIntegrations oidcEnabled={Boolean(bootstrap.oidcEnabled)} />
        ) : tab === 'users' ? (
          <>
            <MfaPolicy organization={bootstrap.organization} notify={notify} onChange={setPolicy} />
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t('Collaborateur')}</th>
                    <th>{t('Rôle')}</th>
                    <th>{t('Protection')}</th>
                    <th>{t('Stockage')}</th>
                    <th>{t('Statut')}</th>
                    <th>
                      <span className="sr-only">{t('Actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <div className="person-cell">
                          <Avatar name={u.name} />
                          <div>
                            <strong>{u.name}</strong>
                            <small>{u.address}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        {u.role === 'owner'
                          ? t('Propriétaire')
                          : u.role === 'admin'
                            ? t('Administrateur')
                            : t('Utilisateur')}
                      </td>
                      <td>
                        {u.mfaEnabled ? (
                          <span className="badge green">{t('MFA activé')}</span>
                        ) : mfaRequired(u.role, policy) ? (
                          <span className="badge orange">{t('À configurer')}</span>
                        ) : (
                          <span className="badge neutral">{t('Facultative')}</span>
                        )}
                      </td>
                      <td>
                        <div className="table-storage">
                          <span>
                            {formatBytes(Number(u.usedBytes ?? 0))} /{' '}
                            {formatBytes(Number(u.quotaBytes ?? 0))}
                          </span>
                          <progress
                            className={`storage-level-${storageLevel(Number(u.usedBytes ?? 0), Number(u.quotaBytes ?? 0))}`}
                            value={Number(u.usedBytes ?? 0)}
                            max={Number(u.quotaBytes ?? 1)}
                          />
                          {storageLevel(Number(u.usedBytes ?? 0), Number(u.quotaBytes ?? 0)) >
                            0 && (
                            <span
                              className={`badge ${storageLevel(Number(u.usedBytes ?? 0), Number(u.quotaBytes ?? 0)) === 100 ? 'red' : 'orange'}`}
                            >
                              {t('{percent} % utilisés', {
                                percent: storageLevel(
                                  Number(u.usedBytes ?? 0),
                                  Number(u.quotaBytes ?? 0),
                                ),
                              })}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`badge ${u.status === 'active' ? 'green' : u.status === 'suspended' ? 'orange' : 'neutral'}`}
                        >
                          {u.status === 'active'
                            ? t('Actif')
                            : u.status === 'suspended'
                              ? t('Suspendu')
                              : t('Invité')}
                        </span>
                      </td>
                      <td>
                        {identityEnabled && u.status === 'invited' && (
                          <button
                            className="text-button"
                            disabled={
                              busy ||
                              identityJobs.some(
                                (j) =>
                                  j.userId === u.id && ['pending', 'sending'].includes(j.status),
                              )
                            }
                            onClick={() => resend(u.id)}
                          >
                            {t('Renvoyer l’invitation')}
                          </button>
                        )}
                        {u.status === 'invited' &&
                          (u.role !== 'owner' || bootstrap.user.role === 'owner') && (
                            <button
                              className="text-button danger"
                              disabled={busy}
                              onClick={() => {
                                if (
                                  !window.confirm(
                                    t(
                                      'Annuler l’invitation de {name} ? Sa boîte, encore vide, sera supprimée et son adresse libérée. Le lien déjà envoyé ne fonctionnera plus.',
                                      { name: u.name },
                                    ),
                                  )
                                )
                                  return;
                                void act(
                                  () => api(`/users/${u.id}/invitation`, { method: 'DELETE' }),
                                  t('Invitation annulée.'),
                                );
                              }}
                            >
                              {t('Annuler l’invitation')}
                            </button>
                          )}
                        {u.id !== bootstrap.user.id && u.status !== 'invited' && (
                          <button
                            className="text-button"
                            disabled={busy}
                            onClick={() => {
                              if (
                                u.status === 'active' &&
                                !window.confirm(
                                  t(
                                    'Suspendre l’accès de {name} ? Ses sessions seront révoquées. La réception des messages continuera.',
                                    { name: u.name },
                                  ),
                                )
                              )
                                return;
                              void act(
                                () =>
                                  send(
                                    `/users/${u.id}`,
                                    { status: u.status === 'active' ? 'suspended' : 'active' },
                                    'PATCH',
                                  ),
                                t('Accès mis à jour.'),
                              );
                            }}
                          >
                            {u.status === 'active' ? t('Suspendre') : t('Réactiver')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : tab === 'invitations' ? (
          <div className="table-wrap">
            <p className="muted" style={{ padding: '0 24px' }}>
              {t(
                'Les invitations sont adressées au contact de récupération personnel. Un envoi non confirmé nécessite une vérification avant de demander un nouveau lien.',
              )}
            </p>
            <table>
              <thead>
                <tr>
                  <th>{t('Compte')}</th>
                  <th>{t('État')}</th>
                  <th>{t('Expiration du lien')}</th>
                </tr>
              </thead>
              <tbody>
                {identityJobs.map((job) => (
                  <tr key={job.id}>
                    <td>{users.find((u) => u.id === job.userId)?.email ?? t('Compte')}</td>
                    <td>
                      {{
                        pending: t('En attente'),
                        sending: t('En cours d’envoi'),
                        sent: t('Invitation transmise'),
                        uncertain: t('Envoi non confirmé'),
                        failed: t('Envoi refusé'),
                        completed: t('Compte activé'),
                        cancelled: t('Annulée'),
                      }[job.status as string] ?? job.status}
                      {job.status === 'sent' &&
                        new Date(job.expiresAt).getTime() < Date.now() &&
                        ' · Lien expiré'}
                    </td>
                    <td>{job.expiresAt ? displayDate(job.expiresAt) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!identityJobs.length && (
              <div className="empty-state">
                <Mail />
                <h3>{t('Aucune invitation IAM')}</h3>
              </div>
            )}
          </div>
        ) : tab === 'domains' ? (
          <div className="domain-list">
            {domains.map((d) => (
              <button className="domain-row" key={d.id} onClick={() => void inspect(d)}>
                <span className="domain-icon">
                  <Globe size={22} />
                </span>
                <div>
                  <strong>{d.name}</strong>
                  <small>
                    {d.lastChecked
                      ? t('Vérifié le {date}', { date: displayDate(d.lastChecked, true) })
                      : t('En attente de vérification DNS')}
                  </small>
                </div>
                <span className={`badge ${d.ownership === 'verified' ? 'green' : 'orange'}`}>
                  {d.ownership === 'verified' ? t('Propriété vérifiée') : t('Propriété à vérifier')}
                </span>
                <span className={`badge ${d.routing === 'ready' ? 'green' : 'neutral'}`}>
                  {d.routing === 'ready' ? t('Routage prêt') : t('Routage à configurer')}
                </span>
                <ArrowUpRight size={19} />
              </button>
            ))}
            {!domains.length && (
              <div className="empty-state">
                <Globe />
                <h3>{t('Ajoutez votre premier domaine')}</h3>
                <p>{t('Connectez le nom de domaine de votre organisation.')}</p>
              </div>
            )}
          </div>
        ) : tab === 'aliases' ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('Adresse de l’alias')}</th>
                  <th>{t('Boîte de destination')}</th>
                  <th>{t('Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {aliases.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <span className="inline-icon">
                        <AtSign size={16} />
                        {a.address}
                      </span>
                    </td>
                    <td>{a.target}</td>
                    <td>
                      <IconButton
                        label={t('Supprimer l’alias {address}', { address: a.address })}
                        disabled={busy}
                        onClick={() => {
                          if (
                            window.confirm(
                              t(
                                'Supprimer l’alias {address} ? Les futurs messages envoyés à cette adresse seront refusés.',
                                { address: a.address },
                              ),
                            )
                          )
                            void act(
                              () => api(`/aliases/${a.id}`, { method: 'DELETE' }),
                              t('Alias supprimé.'),
                            );
                        }}
                      >
                        <Trash2 size={17} />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!aliases.length && (
              <div className="empty-state">
                <AtSign />
                <h3>{t('Aucun alias pour le moment')}</h3>
                <p>{t('Créez par exemple contact@votre-domaine.')}</p>
              </div>
            )}
          </div>
        ) : tab === 'sync' ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('Ressource')}</th>
                  <th>{t('Service')}</th>
                  <th>{t('État')}</th>
                  <th>{t('Action')}</th>
                </tr>
              </thead>
              <tbody>
                {syncJobs.map((job) => (
                  <tr key={job.id}>
                    <td>
                      {users.find((u) => u.id === job.resourceId)?.email ??
                        domains.find((d) => d.id === job.resourceId)?.name ??
                        job.resourceId}
                    </td>
                    <td>
                      {{
                        identity: t('Identité'),
                        mailbox: t('Boîte mail'),
                        alias: t('Alias'),
                        sieve: t('Règles et absence (Sieve)'),
                        list: t('Liste interne'),
                        dkim: t('Signature DKIM du domaine'),
                        domain: t('Raccordement du domaine à la cellule'),
                      }[job.kind as string] ?? job.kind}
                    </td>
                    <td>
                      <span className={`badge ${job.status === 'ready' ? 'green' : 'orange'}`}>
                        {{
                          ready: t('Synchronisé'),
                          pending: t('En attente'),
                          waiting_dns: t('Vérification du domaine requise'),
                          failed: t('À reprendre'),
                        }[job.status as string] ?? job.status}
                      </span>
                      {job.lastError === 'mail_domain_taken' && (
                        <p className="muted">
                          {t(
                            'Ce domaine est déjà raccordé pour une autre organisation. Contactez le support Vename.',
                          )}
                        </p>
                      )}
                    </td>
                    <td>
                      {job.status !== 'ready' && (
                        <button
                          className="secondary small"
                          disabled={busy}
                          onClick={() =>
                            void act(
                              () => send(`/admin/provisioning/${job.id}/retry`, {}),
                              t('Nouvelle tentative planifiée.'),
                            )
                          }
                        >
                          {t('Réessayer')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!syncJobs.length && (
              <div className="empty-state">
                <RefreshCw />
                <h3>{t('Aucune opération de synchronisation')}</h3>
              </div>
            )}
          </div>
        ) : (
          <AuditLog />
        )}
      </section>
      <div className="admin-footnote">
        <ShieldCheck size={17} />
        {t(
          'L’administration permet de gérer les accès et les quotas. Chaque boîte reste accessible à son titulaire.',
        )}
      </div>
      {modal && (
        <Modal
          title={
            modal === 'user'
              ? t('Inviter un collaborateur')
              : modal === 'domain'
                ? t('Ajouter un domaine')
                : t('Créer un alias')
          }
          onClose={() => setModal('')}
        >
          <form className="stack-form" onSubmit={create}>
            {modal === 'domain' ? (
              <>
                <p className="muted">
                  {t(
                    'Vous recevrez les enregistrements DNS pour prouver la propriété du domaine et configurer sa messagerie.',
                  )}
                </p>
                <label>
                  Nom de domaine
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="entreprise.com"
                    required
                  />
                </label>
                {bootstrap.mode === 'local' && (
                  <Notice>
                    {t(
                      'Les domaines terminant par .test peuvent être activés pour les échanges locaux.',
                    )}
                  </Notice>
                )}
              </>
            ) : (
              <>
                {modal === 'user' && (
                  <label>
                    Nom complet
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t('Prénom Nom')}
                      required
                      minLength={2}
                    />
                  </label>
                )}
                <label>
                  {modal === 'user' ? t('Adresse professionnelle') : t('Adresse de l’alias')}
                  <div className="split-field">
                    <input
                      value={local}
                      onChange={(e) => setLocal(e.target.value)}
                      placeholder={modal === 'user' ? 'prenom' : 'contact'}
                      required
                    />
                    <span>@</span>
                    <select value={domain} onChange={(e) => setDomain(e.target.value)} required>
                      {domains.map((d) => (
                        <option value={d.id} key={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </label>
                {modal === 'user' ? (
                  <>
                    <label>
                      {t('Adresse de récupération indépendante')}
                      <input
                        type="email"
                        value={recoveryEmail}
                        onChange={(e) => setRecoveryEmail(e.target.value)}
                        placeholder={t('prenom@autre-domaine.com')}
                        required
                      />
                      <small>
                        {t('L’invitation sera à transmettre par un canal déjà accessible.')}
                      </small>
                    </label>
                    <label>
                      {t('Langue des courriels et de l’invitation')}
                      <select
                        value={inviteLocale}
                        onChange={(e) => setInviteLocale(e.target.value as 'fr' | 'en')}
                      >
                        <option value="fr">{t('Français')}</option>
                        <option value="en">{t('Anglais')}</option>
                      </select>
                    </label>
                    <label>
                      {t('Rôle')}
                      <select value={role} onChange={(e) => setRole(e.target.value)}>
                        <option value="user">{t('Utilisateur')}</option>
                        <option value="admin">{t('Administrateur')}</option>
                        {bootstrap.user.role === 'owner' && (
                          <option value="owner">{t('Propriétaire')}</option>
                        )}
                      </select>
                    </label>
                    {role !== 'user' && (
                      <label>
                        <input
                          type="checkbox"
                          checked={withMailbox}
                          onChange={(e) => setWithMailbox(e.target.checked)}
                        />
                        {t('Créer aussi une boîte mail (une unité)')}
                      </label>
                    )}
                  </>
                ) : (
                  <label>
                    {t('Boîte de destination')}
                    <select value={target} onChange={(e) => setTarget(e.target.value)}>
                      {users
                        .filter((u) => u.mailboxId)
                        .map((u) => (
                          <option value={u.mailboxId} key={u.id}>
                            {u.address}
                          </option>
                        ))}
                    </select>
                  </label>
                )}
              </>
            )}
            {error && <Notice type="error">{error}</Notice>}
            <div className="modal-actions">
              <button type="button" className="secondary" onClick={() => setModal('')}>
                {t('Annuler')}
              </button>
              <button className="primary" disabled={busy}>
                {busy
                  ? t('Enregistrement…')
                  : modal === 'user'
                    ? t('Créer l’invitation')
                    : modal === 'domain'
                      ? t('Ajouter le domaine')
                      : t('Créer l’alias')}
              </button>
            </div>
          </form>
        </Modal>
      )}
      {invitation && (
        <Modal
          title={
            invitation.invitationUrl ? t('L’invitation est prête') : t('Compte en préparation')
          }
          onClose={() => setInvitation(null)}
        >
          <div className="stack-form">
            {!invitation.invitationUrl ? (
              <Notice>
                {t(
                  'Le compte {address} est enregistré. Une invitation sera transmise à {contact} après sa préparation. Suivez son envoi dans Invitations. Le lien expire après 72 heures ; le collaborateur choisira son mot de passe et configurera sa double authentification.',
                  { address: invitation.address, contact: invitation.contact },
                )}
              </Notice>
            ) : (
              <>
                <Notice type="success">
                  {t(
                    'La boîte {address} est créée. Le collaborateur devra choisir son mot de passe et configurer le MFA.',
                    { address: invitation.address },
                  )}
                </Notice>
                <p>
                  {t('Transmettez ce lien à')} <strong>{invitation.contact}</strong>{' '}
                  {t(
                    'par un canal existant. Il expire dans 72 heures et ne peut être utilisé qu’une fois.',
                  )}
                </p>
                <label>
                  {t('Lien d’invitation')}
                  <input value={invitation.invitationUrl} readOnly />
                </label>
                <button
                  className="primary"
                  onClick={() =>
                    void navigator.clipboard
                      .writeText(invitation.invitationUrl)
                      .then(() => notify(t('Lien copié.')))
                      .catch(() => notify(t('Sélectionnez le lien pour le copier.')))
                  }
                >
                  <Copy size={17} />
                  {t('Copier le lien')}
                </button>
              </>
            )}
          </div>
        </Modal>
      )}
      {selectedDomain && (
        <Modal title={selectedDomain.name} onClose={() => setSelectedDomain(null)} wide>
          <div className="stack-form">
            <div className="button-row">
              <span className={`badge ${selectedDomain.routing === 'ready' ? 'green' : 'orange'}`}>
                {selectedDomain.routing === 'ready'
                  ? t('Domaine prêt')
                  : t('Configuration en cours')}
              </span>
              <button
                className="secondary"
                disabled={busy}
                onClick={() => void verify(selectedDomain)}
              >
                <RefreshCw size={16} className={busy ? 'spin' : ''} />
                {t('Vérifier maintenant')}
              </button>
              {selectedDomain.ownership === 'expired' && (
                <button
                  className="secondary"
                  onClick={() =>
                    void act(
                      () => send(`/domains/${selectedDomain.id}/renew`, {}),
                      'Défi renouvelé.',
                    ).then((r) => r && setSelectedDomain(r))
                  }
                >
                  {t('Renouveler le défi')}
                </button>
              )}
            </div>
            <p className="muted">
              {t(
                'Ajoutez ces valeurs chez votre fournisseur DNS. Pour SPF, adaptez l’enregistrement existant afin de conserver vos autres expéditeurs autorisés.',
              )}
            </p>
            {(selectedDomain as any).localOnly && (
              <Notice>
                {t('Domaine de test local : ces valeurs ne sont pas publiées sur Internet.')}
              </Notice>
            )}
            {selectedDomain.records.map((r, i) => (
              <div className="dns-record" key={i}>
                <header>
                  <strong>{r.label}</strong>
                  <span className={`badge ${r.ok ? 'green' : 'neutral'}`}>
                    {r.ok ? t('Vérifié') : t('À vérifier')}
                  </span>
                </header>
                <small>
                  {r.type} · {r.name}
                </small>
                <div className="dns-value">
                  <code>{r.expected}</code>
                  <IconButton
                    label={`Copier ${r.label}`}
                    onClick={() =>
                      void navigator.clipboard
                        .writeText(r.expected)
                        .then(() => notify(t('Valeur copiée.')))
                        .catch(() => notify(t('Sélectionnez la valeur pour la copier.')))
                    }
                  >
                    <Copy size={16} />
                  </IconButton>
                </div>
                {r.actual.length > 0 && !r.ok && (
                  <small>{t('Valeur observée : {value}', { value: r.actual.join(' · ') })}</small>
                )}
              </div>
            ))}
            {error && <Notice type="error">{error}</Notice>}
          </div>
        </Modal>
      )}
    </main>
  );
}
export function Platform({ notify }: { notify: (s: string) => void }) {
  const [data, setData] = useState<any>(),
    [error, setError] = useState(''),
    [modal, setModal] = useState(false),
    [busy, setBusy] = useState(false),
    [result, setResult] = useState<any>(),
    [limitsFor, setLimitsFor] = useState<any>();
  const load = () =>
    api('/platform')
      .then(setData)
      .catch((e) => setError(e.message));
  useEffect(() => {
    void load();
  }, []);
  return (
    <main className="workspace-page">
      <header className="page-heading">
        <div>
          <span className="eyebrow">{t('CONSOLE VENAME')}</span>
          <h1>{t('La plateforme, en un regard.')}</h1>
          <p>{t('Organisations, opérations et états des envois.')}</p>
        </div>
        <button className="primary" onClick={() => setModal(true)}>
          <Plus size={17} />
          {t('Créer une organisation')}
        </button>
      </header>
      {error && <Notice type="error">{error}</Notice>}
      {!data ? (
        <Loading />
      ) : (
        <>
          <Operations />
          <PlatformSubscriptions organizations={data.organizations} />
          <div className="stat-grid">
            <div className="stat-card">
              <Server />
              <div>
                <small>{t('Organisations')}</small>
                <strong>{data.organizations.length}</strong>
              </div>
            </div>
            <div className="stat-card">
              <Users />
              <div>
                <small>{t('Boîtes provisionnées')}</small>
                <strong>
                  {data.organizations.reduce((s: number, o: any) => s + o.mailboxes, 0)}
                </strong>
              </div>
            </div>
            <div className="stat-card">
              <Clock />
              <div>
                <small>{t('Envois en attente')}</small>
                <strong>
                  {data.queue
                    .filter((q: any) => ['pending', 'deferred', 'unknown'].includes(q.status))
                    .reduce((s: number, q: any) => s + q.count, 0)}
                </strong>
              </div>
            </div>
          </div>
          <section className="admin-card">
            <div className="section-heading">
              <h2>{t('Organisations')}</h2>
              <button className="secondary" onClick={() => void load()}>
                <RefreshCw size={16} />
                {t('Actualiser')}
              </button>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t('Organisation')}</th>
                    <th>{t('Offre')}</th>
                    <th>{t('Domaines')}</th>
                    <th>{t('Boîtes')}</th>
                    <th>{t('Volume utilisé')}</th>
                    <th>{t('Limites d’usage')}</th>
                    <th>{t('État')}</th>
                    <th>{t('Collaboration Pro pilote')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.organizations.map((o: any) => (
                    <tr key={o.id}>
                      <td>
                        <strong>{o.name}</strong>
                      </td>
                      <td>{t('Pilote')}</td>
                      <td>
                        <div className="organization-domains">
                          {o.domains.length ? (
                            o.domains.map((domain: any) => (
                              <span key={domain.name}>
                                <strong>{domain.name}</strong>
                                <small>
                                  {domain.ownership === 'verified'
                                    ? domain.routing === 'ready'
                                      ? t('Routage prêt')
                                      : t('Vérifié')
                                    : t('En attente de vérification DNS')}
                                </small>
                              </span>
                            ))
                          ) : (
                            <small>{t('Aucun domaine')}</small>
                          )}
                        </div>
                      </td>
                      <td>{o.mailboxes}</td>
                      <td>{formatBytes(Number(o.usedBytes))}</td>
                      <td>
                        {t('{aliases} alias par boîte · {domains} domaines', {
                          aliases: o.limits.aliasesPerMailbox,
                          domains: o.limits.domains,
                        })}{' '}
                        <button
                          className="text-button"
                          onClick={() => setLimitsFor({ ...o, draft: { ...o.limits } })}
                        >
                          {t('Modifier')}
                        </button>
                      </td>
                      <td>
                        <span className={`badge ${o.status === 'active' ? 'green' : 'orange'}`}>
                          {o.status === 'active' ? t('Active') : t('En préparation')}
                        </span>
                      </td>
                      <td>
                        <button
                          className="secondary"
                          disabled={busy}
                          onClick={async () => {
                            setBusy(true);
                            setError('');
                            try {
                              await send(
                                `/organizations/${o.id}/collaboration`,
                                { enabled: !o.pro_collaboration },
                                'PUT',
                              );
                              await load();
                              notify(
                                o.pro_collaboration
                                  ? t('Collaboration Pro désactivée.')
                                  : t('Collaboration Pro activée pour le pilote.'),
                              );
                            } catch (e: any) {
                              setError(e.message);
                            } finally {
                              setBusy(false);
                            }
                          }}
                        >
                          {o.pro_collaboration
                            ? t('Désactiver le pilote Pro')
                            : t('Activer le pilote Pro')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <section className="admin-card">
            <div className="section-heading">
              <h2>{t('États des envois')}</h2>
            </div>
            <div className="queue-list">
              {data.queue.length ? (
                data.queue.map((q: any) => (
                  <div key={q.status}>
                    <span>{q.status}</span>
                    <strong>{q.count}</strong>
                  </div>
                ))
              ) : (
                <p className="muted">{t('Aucun envoi enregistré pour le moment.')}</p>
              )}
            </div>
          </section>
          <section className="admin-card">
            <div className="section-heading">
              <h2>{t('Opérations de provisioning')}</h2>
            </div>
            <div className="audit-list">
              {data.operations.length ? (
                data.operations.map((o: any) => (
                  <div className="audit-row" key={o.id}>
                    <Clock size={17} />
                    <div>
                      <strong>{o.kind}</strong>
                      <small>{o.id}</small>
                    </div>
                    <span className="badge orange">{o.status}</span>
                  </div>
                ))
              ) : (
                <p className="muted">
                  {t('Les prochaines créations d’organisations apparaîtront ici.')}
                </p>
              )}
            </div>
          </section>
        </>
      )}
      {modal && (
        <Modal title={t('Créer une organisation')} onClose={() => setModal(false)}>
          <form
            className="stack-form"
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              const f = new FormData(e.currentTarget);
              try {
                const r = await send('/organizations', Object.fromEntries(f), 'POST', {
                  'Idempotency-Key': crypto.randomUUID(),
                });
                setResult(r);
                setModal(false);
                notify(t('Organisation créée, en attente DNS.'));
                await load();
              } catch (e: any) {
                setError(e.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            {[
              ['name', 'Nom de l’organisation'],
              ['externalRef', 'Référence de commande'],
              ['domain', 'Domaine'],
              ['adminName', 'Nom de l’administrateur'],
              ['adminContact', 'Contact indépendant de l’administrateur'],
            ].map(([name, label]) => (
              <label key={name}>
                {t(label)}
                <input name={name} type={name === 'adminContact' ? 'email' : 'text'} required />
              </label>
            ))}
            {error && <Notice type="error">{error}</Notice>}
            <button className="primary" disabled={busy}>
              {t('Créer l’organisation')}
            </button>
          </form>
        </Modal>
      )}
      {result && (
        <Modal title={t('Organisation créée')} onClose={() => setResult(undefined)}>
          <div className="stack-form">
            <Notice type="success">{t('Le domaine attend sa vérification DNS.')}</Notice>
            {result.invitationUrl ? (
              <>
                <label>
                  {t('Invitation de l’administrateur')}
                  <input value={result.invitationUrl} readOnly />
                </label>
                <button
                  className="primary"
                  onClick={() =>
                    void navigator.clipboard
                      .writeText(result.invitationUrl)
                      .then(() => notify(t('Invitation copiée.')))
                  }
                >
                  <Copy size={16} />
                  {t('Copier')}
                </button>
              </>
            ) : (
              <Notice>
                {t(
                  'L’identité de l’administrateur est en préparation. Son invitation sera envoyée à son contact indépendant. Le lien permettra de choisir son mot de passe et de configurer ses moyens de connexion.',
                )}
              </Notice>
            )}
          </div>
        </Modal>
      )}
      {limitsFor && (
        <Modal
          title={t('Limites d’usage — {name}', { name: limitsFor.name })}
          onClose={() => setLimitsFor(undefined)}
        >
          <form
            className="stack-form"
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              try {
                await send(
                  `/platform/organizations/${limitsFor.id}/limits`,
                  limitsFor.draft,
                  'PATCH',
                );
                notify(t('Limites d’usage enregistrées.'));
                setLimitsFor(undefined);
                await load();
              } catch (err: any) {
                setError(err.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            <p className="modal-body">
              {t(
                'Le catalogue prévoit 30 alias par boîte et 10 domaines raccordés par organisation. Relevez ces limites sans frais après avoir vérifié un usage légitime ; une limite abaissée conserve les alias et domaines existants.',
              )}
            </p>
            <label>
              {t('Alias par boîte')}
              <input
                type="number"
                min={1}
                max={1000}
                required
                value={limitsFor.draft.aliasesPerMailbox}
                onChange={(e) =>
                  setLimitsFor({
                    ...limitsFor,
                    draft: { ...limitsFor.draft, aliasesPerMailbox: Number(e.target.value) },
                  })
                }
              />
            </label>
            <label>
              {t('Domaines raccordés')}
              <input
                type="number"
                min={1}
                max={200}
                required
                value={limitsFor.draft.domains}
                onChange={(e) =>
                  setLimitsFor({
                    ...limitsFor,
                    draft: { ...limitsFor.draft, domains: Number(e.target.value) },
                  })
                }
              />
            </label>
            <div className="modal-actions">
              <button type="button" className="secondary" onClick={() => setLimitsFor(undefined)}>
                {t('Annuler')}
              </button>
              <button className="primary" disabled={busy}>
                {t('Enregistrer')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </main>
  );
}
